import { CircleCheck, CircleX, CloudDownload } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { Badge, Group, Pagination, Text, Tooltip } from "@mantine/core";
import { Navigate } from "react-router-dom";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import { AuthContext } from "../../../providers/AuthProvider";
import EmployeeImportJobTableEmptyStateContainer from "./EmployeeImportJobTableEmptyStateContainer";
import { employeeImportJobConfig } from "../../../utils/Theme";
import useEmployeeImportJobListApiService from "../services/api/EmployeeImportJobListApiService";
import EmployeeImportJob, {
  EmployeeImportJobStatus,
} from "../services/model/EmployeeImportJob";
import ListItemActions, {
  ListItemActionProps,
  ListItemActionsProps,
} from "../../../themes/elements/ListItemActions";
import CancelEmployeeImportJobHook from "../hooks/CancelEmployeeImportJobHook";
import FinalizeEmployeeImportJobHook from "../hooks/FinalizeEmployeeImportJobHook";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";
import { dateFormat } from "../../../utils/DateTime";
import DownloadResultsFileImportJobHook from "../hooks/DownloadResultsFileImportJobHook";
import DownloadValidationFileImportJobHook from "../hooks/DownloadValidationFileImportJobHook";

type EmployeeImportJobTableProps = {
  selection: EmployeeImportJob[];
  onSelection: React.Dispatch<React.SetStateAction<EmployeeImportJob[]>>;
};

const EmployeeImportJobTable: React.FC<EmployeeImportJobTableProps> = ({
  selection,
  onSelection,
}) => {
  const auth = useContext(AuthContext);
  const [activePage, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const { isLoading, isError, error, data } =
    useEmployeeImportJobListApiService({
      page: activePage,
      pagination: true,
    });

  const { cancelEmployeeImportJob } = CancelEmployeeImportJobHook();
  const { finalizeEmployeeImportJob } = FinalizeEmployeeImportJobHook();
  const { downloadResultsFile } = DownloadResultsFileImportJobHook();
  const { downloadValidationFile } = DownloadValidationFileImportJobHook();

  const tableMaxHeight = ResponsiveHeight(
    "",
    "80vh",
    "80vh",
    "80vh",
    "75vh",
    "80vh"
  );

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  if (loading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (!data) {
    return <EmployeeImportJobTableEmptyStateContainer />;
  }

  if (!data?.results?.length) {
    return <EmployeeImportJobTableEmptyStateContainer />;
  }

  const cancelEmployeeImportJobProcess = (job: EmployeeImportJob): void => {
    cancelEmployeeImportJob(job.id);
  };

  const approveAndRelease = (job: EmployeeImportJob): void => {
    finalizeEmployeeImportJob(auth.employer.id, job.id, job.source_id);
  };

  const acceptResultFileDownload = (id: number): void => {
    downloadResultsFile(id);
  };

  const acceptValidationFileDownload = (id: number): void => {
    downloadValidationFile(id);
  };

  const renderActions = (job: EmployeeImportJob): ListItemActionsProps => {
    const actions: ListItemActionProps[] = [
      {
        id: "cancel-employee-import-job",
        verify: {
          show: true,
          message: `Are you sure you want to cancel this import job?`,
          button: "Confirm",
        },
        callback: () => cancelEmployeeImportJobProcess(job),
        element: (
          <Tooltip label="Cancel">
            <CircleX />
          </Tooltip>
        ),
        active: [
          EmployeeImportJobStatus.CREATED,
          EmployeeImportJobStatus.ANALYSING,
          EmployeeImportJobStatus.ANALYSED,
        ].includes(job.status),
      },
      {
        id: "finalize-employee-import-job",
        verify: {
          show: true,
          message: "Are you sure you want to finalize this import job?",
          button: "Send",
        },
        callback: () => approveAndRelease(job),
        element: (
          <Tooltip label="Finalize">
            <CircleCheck />
          </Tooltip>
        ),
        active: [EmployeeImportJobStatus.ANALYSED].includes(job.status),
      },
      {
        id: "download-validation-errors-file",
        verify: {
          show: true,
          message:
            "Are you sure you want to download the validation errors file?",
          button: "Download validation errors file",
        },
        callback: () => acceptValidationFileDownload(job.id),
        element: (
          <Tooltip label="Download validations">
            <CloudDownload />
          </Tooltip>
        ),
        active: [
          EmployeeImportJobStatus.COMPLETED,
          EmployeeImportJobStatus.ANALYSING,
          EmployeeImportJobStatus.ANALYSED,
          EmployeeImportJobStatus.PROCESSING,
          EmployeeImportJobStatus.COMPLETED_WITH_ERRORS,
          EmployeeImportJobStatus.FAILED,
          EmployeeImportJobStatus.CANCELLED,
        ].includes(job.status),
      },
      {
        id: "download-result-file",
        verify: {
          show: true,
          message: "Are you sure you want to download the result file?",
          button: "Download result file",
        },
        callback: () => acceptResultFileDownload(job.id),
        element: (
          <Tooltip label="Download results">
            <CloudDownload />
          </Tooltip>
        ),
        active: [
          EmployeeImportJobStatus.COMPLETED,
          EmployeeImportJobStatus.COMPLETED_WITH_ERRORS,
        ].includes(job.status),
      },
    ];
    return { elements: actions };
  };

  const filteredData = data?.results;

  return (
    <>
      <DirectoryTable
        tableMaxHeight={tableMaxHeight}
        data={filteredData}
        selection={selection}
        onSelect={onSelection}
        headers={[
          "Uploaded by",
          "Date uploaded",
          "Employees to create",
          "Employees to update",
          "Employees created",
          "Employees updated",
          "Failed rows",
          "Status",
          "Actions",
        ]}
        headersStyles={{
          Total: { width: "25px" },
          Matched: { width: "25px" },
          Errors: { width: "25px" },
        }}
        rows={filteredData?.map((it, index) => ({
          name: (
            <Group spacing="sm">
              <Text size="sm">
                {`${it.user_uploaded.first_name} ${
                  it.user_uploaded && it.user_uploaded.last_name
                    ? it.user_uploaded.last_name[0]
                    : ""
                }.`}
              </Text>
            </Group>
          ),
          //   source: it.source,
          date_created: dateFormat(it.date_created),
          will_create: it.will_create_count,
          will_update: it.will_update_count,
          created: it.did_create_count, // TODO: total / valid / invalid need to be changed as it is only for flatfile
          updated: it.did_update_count,
          failed: it.failure_count,
          status: (
            <Badge
              id={`employee-import-table-status${index}`}
              style={{
                color: employeeImportJobConfig[it.status || ""],
                backgroundColor: employeeImportJobConfig[it.status || ""],
              }}
              color={employeeImportJobConfig[it.status || ""]}
            >
              {it?.status?.replace("_", " ") || ""}
            </Badge>
          ),
          actions: <ListItemActions {...renderActions(it)} />,
        }))}
      />

      {data.results.length > 0 && (
        <Pagination
          position="right"
          mt={16}
          onChange={setPage}
          total={data.pages}
          initialPage={1}
          page={activePage}
        />
      )}
    </>
  );
};

export default EmployeeImportJobTable;
